import { OnboardingLayout, PageHeader } from "@/components/layouts/OnboardingLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import { BodyMedium, H2, H3, H4, P, Small } from "@/components/ui/typography"
import { Flex } from "@chakra-ui/react"
import { Briefcase, DollarSign, FileDown, MapPin, Plus } from "lucide-react"
import React from "react"
import {
	useReactTable,
	getCoreRowModel,
	getPaginationRowModel,
	flexRender,
	createColumnHelper,
} from "@tanstack/react-table"
import { ReportPagination } from "./Pagination"
import { ReportHeader } from "./Header"
import { AddVehicleDialogContent } from "./AddVehicleDialog"
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { VehicleSummaryTable } from "./VehicleSummaryTable"
import { VehicleSummary } from "./VehicleSummary"
import { PaywallBanner } from "./PaywallBanner"
import { VehicleModel } from "@/state/draft/vehicle"
import { mi2, num, usd2, usd3 } from "@/utils/formaters"
import { sum } from "@/utils/array"
import { Paywall } from "./Paywall"

export type ReportTrip = {
	date: string
	purpose: string
	startAddress: string
	finishAddress: string
	rate: number
	distance: number
	amount: number
}

type ReportOverviewProps = {
	blockedByPaywall: boolean
	onPaywallOpen: () => void
	vehicle?: VehicleModel
	isVehicleUpdating: boolean
	onVehicleChange: (vehicle: VehicleModel) => void
	onAddVehicleClick: () => void
	vehicleSummary: {
		vehicles: {
			name: string
			odometer: number
			businessMiles: number
			rate: number[]
			businessValue: number
		}[]
		total: number
	}
	onEditReport: () => void
	onUnlock: () => void
	onExportReport: () => void
	onNewReport: () => void
	reportFee: number
	reportDateRangeText: string
	monthsCount: number
	trips: ReportTrip[]
	onPayClick: () => void
	reportId: string
	isProcessingPayment: boolean
}

const columnHelper = createColumnHelper<ReportTrip>()

const columns = [
	columnHelper.accessor("date", {
		header: "Date of Trip",
		cell: (info) => info.getValue(),
		size: 12,
	}),
	columnHelper.accessor("purpose", {
		header: "Purpose",
		cell: (info) => info.getValue(),
		size: 12,
	}),
	columnHelper.accessor("startAddress", {
		header: "Start Address",
		cell: (info) => info.getValue(),
		size: 28,
	}),
	columnHelper.accessor("finishAddress", {
		header: "Finish Address",
		cell: (info) => info.getValue(),
		size: 28,
	}),
	columnHelper.accessor("rate", {
		header: "Rate",
		cell: (info) => usd3(info.getValue()),
		size: 10,
	}),
	columnHelper.accessor("distance", {
		header: "Distance",
		cell: (info) => <div className="text-right">{mi2(info.getValue())}</div>,
		size: 14,
	}),
	columnHelper.accessor("amount", {
		header: "Amount",
		cell: (info) => <div className="text-right">{usd2(info.getValue())}</div>,
		size: 10,
	}),
]

export const ReportOverviewScreen: React.FC<ReportOverviewProps> = (props) => {
	const [paywallOpen, setPaywallOpen] = React.useState(false)

	const handleEdit = () => {
		props.onEditReport()
	}

	const handleExport = () => {
		if (!props.blockedByPaywall) {
			return props.onExportReport()
		}
		setPaywallOpen(true)
	}

	const handleUnlock = () => {
		props.onUnlock()
		setPaywallOpen(true)
	}

	const table = useReactTable({
		data: props.trips,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		initialState: {
			pagination: {
				pageSize: 12,
			},
		},
	})
	function handleNew() {
		props.onNewReport()
	}

	return (
		<div className="min-h-screen bg-gray-50 px-6 py-8">
			<div className="container mx-auto flex flex-col gap-6">
				<PageHeader />
				<div className="max-w-6xl mx-auto">
					<ReportHeader
						isLocked={props.blockedByPaywall}
						onEdit={handleEdit}
						onExport={handleExport}
						onUnlock={handleUnlock}
						unlockVisible={props.blockedByPaywall}
						newVisible={!props.blockedByPaywall}
						onNew={handleNew}
					/>
					{/* Report Title */}
					<Card className="p-6">
						<div>
							<H2>Mileage Report</H2>
							<P className="text-muted-foreground">
								{props.reportDateRangeText} ({props.monthsCount} Months)
							</P>
						</div>

						{/* Stats Cards */}
						<VehicleSummary
							businessTrips={num(props.trips.length)}
							totalSavings={usd2(sum(props.trips, (x) => x.amount))}
							totalMileage={mi2(sum(props.trips, (x) => x.distance))}
						/>

						{/* Report Preview */}
						<div className="">
							{!props.vehicle && (
								<>
									<div>
										<H4>Report Preview</H4>
										<Small className="text-muted-foreground font-normal">
											Review your report to ensure it meets your
											needs before purchasing the full version.
										</Small>
									</div>
									<Dialog>
										<DialogTrigger asChild>
											<Button
												onClick={() => {
													props.onAddVehicleClick()
												}}
												variant="outline"
												className="gap-2 mt-1"
												size={"lg"}
											>
												Add Vehicle Information
												<Plus />
											</Button>
										</DialogTrigger>
										<AddVehicleDialogContent
											submiting={props.isVehicleUpdating}
											vehicle={props.vehicle}
											onSubmit={props.onVehicleChange}
										/>
									</Dialog>
								</>
							)}

							{props.vehicle && (
								<div>
									<P className="font-bold mb-4">Vehicle Summary</P>
									<VehicleSummaryTable
										isVehicleUpdating={props.isVehicleUpdating}
										vehicleModel={props.vehicle}
										vehicles={props.vehicleSummary.vehicles.map(
											(x) => ({
												name: x.name,
												odometer: mi2(x.odometer),
												businessMiles: mi2(x.businessMiles),
												businessValue: usd2(x.businessValue),
												rate: x.rate
													.map((x) => usd3(x))
													.join(" - "),
											})
										)}
										total={usd2(props.vehicleSummary.total)}
										onEditSubmit={props.onVehicleChange}
									/>
								</div>
							)}

							{/* Report Table */}
							<div
								className={cn(
									"relative mt-6",
									props.blockedByPaywall && "pb-[100px]"
								)}
							>
								{props.vehicle && (
									<P className="font-bold mb-4">
										Mileage Log: {props.vehicle.make}{" "}
										{props.vehicle.model} {props.vehicle.year}
									</P>
								)}
								<Table className="rounded-lg">
									<TableHeader>
										{table.getHeaderGroups().map((headerGroup) => (
											<TableRow
												key={headerGroup.id}
												className="bg-slate-200 hover:bg-slate-200"
											>
												{headerGroup.headers.map((header) => (
													<TableHead
														key={header.id}
														className="text-foreground px-4"
														style={{
															width: `${header.column.columnDef.size}%`,
														}}
													>
														{flexRender(
															header.column.columnDef
																.header,
															header.getContext()
														)}
													</TableHead>
												))}
											</TableRow>
										))}
									</TableHeader>
									<TableBody className="rounded-b-lg">
										{table.getRowModel().rows.map((row) => (
											<TableRow
												key={row.id}
												className={
													row.index % 2 === 1
														? "bg-slate-50"
														: ""
												}
											>
												{row.getVisibleCells().map((cell) => (
													<TableCell
														key={cell.id}
														className="px-4"
													>
														{flexRender(
															cell.column.columnDef.cell,
															cell.getContext()
														)}
													</TableCell>
												))}
											</TableRow>
										))}
									</TableBody>
								</Table>
								{!props.blockedByPaywall && (
									<ReportPagination table={table} />
								)}
								{props.blockedByPaywall && (
									<div className="absolute bottom-[100px] left-0 right-0 h-[350px] bg-gradient-to-b from-transparent via-white/60 to-white pointer-events-none"></div>
								)}
							</div>
						</div>
						{/* Footer */}
						{props.blockedByPaywall && (
							<div className="mt-[-140px] relative z-10">
								<PaywallBanner
									price={usd2(props.reportFee)}
									dateRange={`${props.reportDateRangeText} (${props.monthsCount} Months)`}
									onExport={handleExport}
									onUnlock={handleUnlock}
								/>
							</div>
						)}
					</Card>
				</div>
			</div>
			<Dialog open={paywallOpen} onOpenChange={setPaywallOpen}>
				<DialogContent>
					<Paywall
						onPaywallOpen={props.onPaywallOpen}
						buttonText="Unlock now"
						dateRange={props.reportDateRangeText}
						onPayClick={() => {
							props.onPayClick()
						}}
						price={usd2(props.reportFee)}
						securePaymentText="Payment secured by Stripe"
						subtitle={`Mileage Log Report for ${props.monthsCount} Months`}
						title="Unlock Your Full Report for"
						isProcessingPayment={props.isProcessingPayment}
					/>
				</DialogContent>
			</Dialog>
		</div>
	)
}
